"use client";
import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { useCostCodeMap } from "./useCostCodeMap";
import SubAgreementModal from "./SubAgreementModal";

const API_BASE = "https://erp-production-72da01c8e651.herokuapp.com/api";

// ─── Helpers ────────────────────────────────────────────────────────────────
const fmt = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;

const fmtExact = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const fmtQty = (n) =>
  Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const pct = (a, b) => (b > 0 ? Math.min(100, (a / b) * 100) : 0);
const cls = (...c) => c.filter(Boolean).join(" ");

const isCoverageUOM = (uom = "") => {
  const u = String(uom).trim().toLowerCase();
  return (
    u === "sf" ||
    u === "lf" ||
    u === "sq ft" ||
    u === "lin ft" ||
    u === "sqft" ||
    u === "linft" ||
    u.includes("square") ||
    u.includes("linear") ||
    u === "sheet"
  );
};

// ─── Cost bar ───────────────────────────────────────────────────────────────
function CostBar({ budget, charged, pending, height = "h-2" }) {
  const chargedPct = pct(charged, budget);
  const pendingPct = pct(pending, budget);
  const overBudget = charged + pending > budget && budget > 0;
  return (
    <div
      className={`w-full bg-gray-700 rounded-full ${height} overflow-hidden flex`}
    >
      <div
        className={`h-full transition-all duration-500 ${overBudget ? "bg-red-500" : "bg-green-500"}`}
        style={{ width: `${Math.min(100, chargedPct)}%` }}
      />
      <div
        className="h-full bg-yellow-400/80 transition-all duration-500"
        style={{ width: `${Math.min(100 - chargedPct, pendingPct)}%` }}
      />
    </div>
  );
}

// ─── Mini summary tile ──────────────────────────────────────────────────────
function SummaryTile({
  label,
  value,
  sub,
  color = "text-white",
  small = false,
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold">
        {label}
      </span>
      <span
        className={`font-bold tabular-nums ${small ? "text-sm" : "text-lg"} ${color}`}
      >
        {value}
      </span>
      {sub && <span className="text-[10px] text-gray-400">{sub}</span>}
    </div>
  );
}

// ─── Section header (Labour / Materials parent section) ────────────────────
function SectionHeader({
  title,
  budget,
  charged,
  pending,
  remaining,
  count,
  icon,
}) {
  const chargedPct = pct(charged, budget);
  const pendingPct = pct(pending, budget);
  const overBudget = charged > budget && budget > 0;
  return (
    <div className="mb-3">
      <div className="flex items-center gap-3 mb-2">
        <span className="text-lg">{icon}</span>
        <h2 className="text-base font-bold text-white tracking-tight">
          {title}
        </h2>
        <span className="text-xs text-gray-400">
          {count} item{count !== 1 ? "s" : ""}
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3 bg-gray-800 rounded-lg p-4 border border-gray-700">
        <SummaryTile
          label="Total Estimated Project Cost"
          value={fmt(budget)}
          color="text-gray-200"
        />
        <SummaryTile
          label="Cost Incurred"
          value={fmt(charged)}
          sub={`${chargedPct.toFixed(1)}% of budget`}
          color={overBudget ? "text-red-400" : "text-green-400"}
        />
        <SummaryTile
          label="Committed Costs"
          value={fmt(pending)}
          sub={`${pendingPct.toFixed(1)}%`}
          color="text-yellow-400"
        />
        <SummaryTile
          label="Remaining"
          value={fmt(remaining)}
          color="text-orange-300"
        />
      </div>
      <CostBar
        budget={budget}
        charged={charged}
        pending={pending}
        height="h-3"
      />
    </div>
  );
}

// ─── Generic accordion card ─────────────────────────────────────────────────
function AccordionCard({ isOpen, onToggle, header, children }) {
  return (
    <div
      className={`rounded-lg border transition-colors ${isOpen ? "border-gray-500 bg-gray-800" : "border-gray-700 bg-gray-800/60 hover:border-gray-600"}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between text-left"
      >
        {header}
        <svg
          className={`w-4 h-4 text-gray-300 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      {isOpen && (
        <div className="px-4 pb-4 border-t border-gray-700">{children}</div>
      )}
    </div>
  );
}

// ─── Type badge ─────────────────────────────────────────────────────────────
function TypeBadge({ type }) {
  const cfg = {
    piecework: "bg-purple-700 text-purple-200 border-purple-500",
    union_piecework: "bg-teal-700 text-teal-200 border-teal-500",
    hourly: "bg-blue-700 text-blue-200 border-blue-500",
    union_hourly: "bg-cyan-700 text-cyan-200 border-cyan-500",
    subcontractor: "bg-violet-700 text-violet-200 border-violet-500",
  };
  const label = (type || "").replace("_", " ");
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border ${cfg[type] || "bg-gray-600 text-gray-200 border-gray-500"}`}
    >
      {label}
    </span>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// SECTION GROUPING
// ════════════════════════════════════════════════════════════════════════════
//
// Groups items by `sectionNumber`, computes a per-section rollup, and renders
// a list of collapsible Section rows. Each row exposes its rolled-up money
// + qty in the header; clicking expands to show the item table.
//
// The parent drill-down (Piecework / Hourly / Subcontractor / Material) is
// responsible for telling us how to:
//   1. Roll up each item into per-section totals  (rollupItem)
//   2. Render the item-detail table inside an expanded section  (renderItems)
//
// This keeps section UI consistent everywhere while letting each drill-down
// keep its own item-level columns and math.
// ════════════════════════════════════════════════════════════════════════════

// Build per-section summary objects from items.
//   rollupItem(item)  =>  { budget, charged, pending, qty?, ... }
function groupItemsBySection(items, rollupItem, resolveName) {
  const map = new Map();
  for (const item of items) {
    const secNum = (item.sectionNumber || "").trim() || "(unassigned)";
    if (!map.has(secNum)) {
      map.set(secNum, {
        sectionNumber: secNum,
        sectionName: secNum !== "(unassigned)" ? resolveName(secNum) : null,
        items: [],
        budget: 0,
        charged: 0,
        pending: 0,
        qty: 0,
        installed: 0,
        pendingQty: 0,
      });
    }
    const sec = map.get(secNum);
    sec.items.push(item);
    const r = rollupItem(item);
    sec.budget += r.budget || 0;
    sec.charged += r.charged || 0;
    sec.pending += r.pending || 0;
    sec.qty += r.qty || 0;
    sec.installed += r.installed || 0;
    sec.pendingQty += r.pendingQty || 0;
  }
  return [...map.values()].sort((a, b) =>
    (a.sectionNumber || "").localeCompare(b.sectionNumber || ""),
  );
}

// Section accordion row — collapsible, shows rollup + cost bar in the header
function SectionAccordionRow({
  section,
  isOpen,
  onToggle,
  renderItems,
  idPrefix,
}) {
  const remaining = Math.max(
    0,
    section.budget - section.charged - section.pending,
  );
  const chargedPct = pct(section.charged, section.budget);
  const overBudget = section.charged > section.budget && section.budget > 0;
  const isUnbudgeted = section.sectionNumber === "(unassigned)";
  const installedPct = pct(section.installed, section.qty);
  const showQty = section.qty > 0 || section.installed > 0;

  return (
    <div
      className={cls(
        "rounded-md border transition-colors",
        isOpen
          ? "border-orange-500/40 bg-gray-900/40"
          : "border-gray-700/70 bg-gray-900/20 hover:border-gray-600",
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-3 py-2.5 flex items-center gap-3 text-left"
      >
        {/* Section badge */}
        <span
          className={cls(
            "text-[10px] font-mono px-2 py-0.5 rounded shrink-0",
            isUnbudgeted
              ? "bg-yellow-900/60 text-yellow-300 border border-yellow-700/40"
              : "bg-indigo-900/60 text-indigo-300 border border-indigo-700/40",
          )}
        >
          §{section.sectionNumber}
        </span>

        {/* Section title + item count */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-200 truncate">
              {section.sectionName || section.sectionNumber}
            </span>
            <span className="text-[10px] text-gray-500 shrink-0">
              {section.items.length} item{section.items.length !== 1 ? "s" : ""}
            </span>
          </div>
          {/* Inline rollup row */}
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            <span className="text-[11px] text-gray-400">
              Budget{" "}
              <span className="text-gray-200 font-medium">
                {fmt(section.budget)}
              </span>
            </span>
            <span className="text-[11px] text-gray-400">
              Incurred{" "}
              <span
                className={`font-medium ${overBudget ? "text-red-400" : "text-green-400"}`}
              >
                {fmt(section.charged)}
              </span>
            </span>
            {section.pending > 0 && (
              <span className="text-[11px] text-gray-400">
                Committed{" "}
                <span className="text-yellow-400 font-medium">
                  {fmt(section.pending)}
                </span>
              </span>
            )}
            <span className="text-[11px] text-gray-400">
              Remaining{" "}
              <span className="text-orange-300 font-medium">
                {fmt(remaining)}
              </span>
            </span>
            {showQty && (
              <span className="text-[11px] text-gray-500">
                ·{" "}
                <span className="text-purple-300 font-medium">
                  {fmtQty(section.installed)}
                </span>
                <span className="text-gray-600">
                  {" "}
                  / {fmtQty(section.qty)} installed
                </span>
              </span>
            )}
          </div>
        </div>

        {/* Mini cost bar */}
        <div className="w-28 shrink-0">
          <div className="flex justify-between text-[9px] text-gray-500 mb-0.5">
            <span>{chargedPct.toFixed(0)}% cost</span>
            {showQty && (
              <span className="text-purple-400/80">
                {installedPct.toFixed(0)}% qty
              </span>
            )}
          </div>
          <CostBar
            budget={section.budget}
            charged={section.charged}
            pending={section.pending}
            height="h-1.5"
          />
        </div>

        {/* Chevron */}
        <svg
          className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="border-t border-gray-700/50 px-3 pt-2 pb-3">
          {renderItems(section.items)}
        </div>
      )}
    </div>
  );
}

// Wraps a list of sections in a stack of collapsible rows.
// `keyPrefix` keeps section-open state distinct between parent drill-downs.
function SectionList({ sections, renderItems, keyPrefix }) {
  const [openSet, setOpenSet] = useState(() => new Set());
  const toggle = (key) =>
    setOpenSet((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  if (sections.length === 0) {
    return (
      <div className="py-8 text-center text-gray-500 text-sm border border-gray-700 rounded">
        No items to display.
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {sections.map((sec) => {
        const key = `${keyPrefix}-${sec.sectionNumber}`;
        return (
          <SectionAccordionRow
            key={key}
            section={sec}
            isOpen={openSet.has(key)}
            onToggle={() => toggle(key)}
            renderItems={renderItems}
            idPrefix={keyPrefix}
          />
        );
      })}
    </div>
  );
}

// ─── Hook: fetch WO items (V3) ──────────────────────────────────────────────
function useWOItems(woId, token) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const woIdStr = useMemo(() => {
    if (!woId) return "";
    if (typeof woId === "string") return woId;
    if (woId._id) return String(woId._id);
    return String(woId);
  }, [woId]);

  useEffect(() => {
    if (!woIdStr) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [itemsRes, histRes, tsRes] = await Promise.all([
          axios.get(`${API_BASE}/workorders3/${woIdStr}/items`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { limit: 1000 },
          }),
          axios
            .get(`${API_BASE}/workorders3/${woIdStr}/log-history`, {
              headers: { Authorization: `Bearer ${token}` },
            })
            .catch(() => ({ data: { historyByBudgetItem: {} } })),
          axios
            .get(`${API_BASE}/workorders3/${woIdStr}/timesheet-summary`, {
              headers: { Authorization: `Bearer ${token}` },
            })
            .catch(() => ({ data: null })),
        ]);
        if (!cancelled)
          setResult({
            items: itemsRes.data.items || [],
            logHistory: histRes.data.historyByBudgetItem || {},
            timesheetSummary: tsRes.data || null,
          });
      } catch (err) {
        console.error("useWOItems error:", err);
        if (!cancelled)
          setResult({ items: [], logHistory: {}, timesheetSummary: null });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [woIdStr, token]);

  return { result, loading };
}

// ─── Hook: fetch PO items + deliveries (V3) ─────────────────────────────────
function usePOItems(poId, token) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const poIdStr = useMemo(() => {
    if (!poId) return "";
    if (typeof poId === "string") return poId;
    if (poId._id) return String(poId._id);
    return String(poId);
  }, [poId]);

  useEffect(() => {
    if (!poIdStr) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [itemsRes, delRes] = await Promise.all([
          axios.get(`${API_BASE}/purchaseOrders3/${poIdStr}/items`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { limit: 1000 },
          }),
          axios
            .get(`${API_BASE}/purchaseOrders3/${poIdStr}/deliveries`, {
              headers: { Authorization: `Bearer ${token}` },
            })
            .catch(() => ({ data: { historyByBudgetItem: {} } })),
        ]);
        if (!cancelled)
          setResult({
            items: itemsRes.data.items || [],
            deliveries: delRes.data.historyByBudgetItem || {},
          });
      } catch (err) {
        console.error("usePOItems error:", err);
        if (!cancelled) setResult({ items: [], deliveries: {} });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [poIdStr, token]);

  return { result, loading };
}

// ════════════════════════════════════════════════════════════════════════════
// SHARED ITEM-LEVEL ROW RENDERERS
// ════════════════════════════════════════════════════════════════════════════
//
// These produce the same item tables that used to live inline in each
// drill-down. They are now passed to <SectionAccordionRow> via renderItems().
// ════════════════════════════════════════════════════════════════════════════

function PieceworkItemsTable({ items }) {
  return (
    <div className="overflow-x-auto rounded border border-gray-700">
      <table className="w-full text-xs">
        <thead className="bg-gray-900/80">
          <tr>
            {[
              "Description",
              "Net Qty",
              "UOM",
              "Charged Qty",
              "Pending Qty",
              "Remaining",
              "Rate",
              "Budget $",
              "Cost Incurred $",
              "Committed $",
              "Progress",
            ].map((h) => (
              <th
                key={h}
                className="px-2 py-2 text-left text-[10px] font-semibold text-gray-400 uppercase whitespace-nowrap"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-700/50">
          {items.map((item, i) => {
            const isBudget = item.source === "budget";
            const isCONew = item.source === "change_order";
            const isUnbudgeted = item.source === "piecework_unbudgeted";
            const origQty = item.requiredQty || item.quantity || 0;
            const netQty = isBudget
              ? origQty + (item.totalCODeltaQty || 0)
              : isCONew
                ? item.totalCODeltaQty || 0
                : item.qtyInstalled || 0;
            const installed = item.qtyInstalled || 0;
            const pendingQty = item.qtyPending || 0;
            const remaining = Math.max(0, netQty - installed - pendingQty);
            const rate = item.unitRate || 0;
            const budget = isBudget
              ? (item.amount || 0) + (item.totalCOAmount || 0)
              : isCONew
                ? item.totalCOAmount || 0
                : 0;
            const chargedAmt = item.amountActual || 0;
            const pendingAmt = item.amountPending || 0;
            const installedPct = pct(installed, netQty);
            const pendingPct2 = pct(pendingQty, netQty);

            return (
              <tr
                key={item._id || i}
                className={cls(
                  "hover:bg-gray-700/30",
                  isCONew && "border-l-2 border-purple-500/40",
                  isUnbudgeted && "border-l-2 border-yellow-500/40",
                )}
              >
                <td
                  className="px-2 py-2 text-gray-200 max-w-[260px] truncate"
                  title={item.costCodeDescription || item.itemDescription}
                >
                  {item.costCodeDescription || item.itemDescription || "—"}
                  {isCONew && (
                    <span className="ml-1 text-[9px] bg-purple-600/60 text-purple-100 px-1 py-0.5 rounded font-semibold">
                      CO NEW
                    </span>
                  )}
                  {isUnbudgeted && (
                    <span className="ml-1 text-[9px] bg-yellow-600/60 text-yellow-100 px-1 py-0.5 rounded font-semibold">
                      UNBUDGETED
                    </span>
                  )}
                </td>
                <td className="px-2 py-2 text-right text-gray-200 font-medium">
                  {isUnbudgeted ? "—" : fmtQty(netQty)}
                </td>
                <td className="px-2 py-2 text-gray-400">{item.uom || "—"}</td>
                <td className="px-2 py-2 text-right text-green-400 font-medium">
                  {installed > 0 ? fmtQty(installed) : "—"}
                </td>
                <td className="px-2 py-2 text-right text-yellow-400">
                  {pendingQty > 0 ? fmtQty(pendingQty) : "—"}
                </td>
                <td className="px-2 py-2 text-right">
                  {isUnbudgeted ? (
                    "—"
                  ) : remaining > 0 ? (
                    <span className="text-orange-300">{fmtQty(remaining)}</span>
                  ) : (
                    <span className="text-green-400 font-bold">✓</span>
                  )}
                </td>
                <td className="px-2 py-2 text-right text-gray-300">
                  {fmtExact(rate)}
                </td>
                <td className="px-2 py-2 text-right text-gray-200">
                  {isUnbudgeted ? "—" : fmt(budget)}
                </td>
                <td className="px-2 py-2 text-right text-green-400 font-semibold">
                  {chargedAmt > 0 ? fmt(chargedAmt) : "—"}
                </td>
                <td className="px-2 py-2 text-right text-yellow-400">
                  {pendingAmt > 0 ? fmt(pendingAmt) : "—"}
                </td>
                <td className="px-2 py-2">
                  {isUnbudgeted ? (
                    <span className="text-[10px] text-yellow-400">
                      {fmtQty(installed)} installed
                    </span>
                  ) : (
                    <div className="flex items-center gap-2">
                      <div className="w-20 bg-gray-700 rounded-full h-1.5 overflow-hidden flex">
                        <div
                          className="h-full bg-green-500"
                          style={{ width: `${Math.min(100, installedPct)}%` }}
                        />
                        <div
                          className="h-full bg-yellow-400/80"
                          style={{
                            width: `${Math.min(100 - installedPct, pendingPct2)}%`,
                          }}
                        />
                      </div>
                      <span
                        className={`text-[10px] font-semibold ${installedPct >= 90 ? "text-green-400" : installedPct >= 50 ? "text-blue-400" : "text-gray-400"}`}
                      >
                        {installedPct.toFixed(0)}%
                      </span>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function BudgetItemsTable({ items }) {
  return (
    <div className="overflow-x-auto rounded border border-gray-700">
      <table className="w-full text-xs">
        <thead className="bg-gray-900/80">
          <tr>
            {[
              "Description",
              "Net Qty",
              "UOM",
              "Rate",
              "Est. Hrs",
              "Budget $",
            ].map((h) => (
              <th
                key={h}
                className="px-2 py-2 text-left text-[10px] font-semibold text-gray-400 uppercase whitespace-nowrap"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-700/50">
          {items.map((item, i) => {
            const origQty = item.requiredQty || item.quantity || 0;
            const netQty =
              item.source === "budget"
                ? origQty + (item.totalCODeltaQty || 0)
                : item.totalCODeltaQty || 0;
            const rate = item.unitRate || 0;
            const budget =
              item.source === "budget"
                ? (item.amount || 0) + (item.totalCOAmount || 0)
                : item.totalCOAmount || 0;
            return (
              <tr key={item._id || i} className="hover:bg-gray-700/30">
                <td
                  className="px-2 py-2 text-gray-200 max-w-[260px] truncate"
                  title={item.costCodeDescription || item.itemDescription}
                >
                  {item.costCodeDescription || item.itemDescription || "—"}
                </td>
                <td className="px-2 py-2 text-right text-gray-200 font-medium">
                  {fmtQty(netQty)}
                </td>
                <td className="px-2 py-2 text-gray-400">{item.uom || "—"}</td>
                <td className="px-2 py-2 text-right text-gray-300">
                  {fmtExact(rate)}
                </td>
                <td className="px-2 py-2 text-right text-gray-400">
                  {(item.laborHours || 0).toFixed(1)}
                </td>
                <td className="px-2 py-2 text-right font-medium text-orange-400">
                  {fmt(budget)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function MaterialItemsTable({ items }) {
  return (
    <div className="overflow-x-auto rounded border border-gray-700">
      <table className="w-full text-xs">
        <thead className="bg-gray-900/80">
          <tr>
            {[
              "Description",
              "Size",
              "Net Qty",
              "UOM",
              "Ordered",
              "Delivered",
              "Pending",
              "Remaining",
              "Unit Cost",
              "Budget $",
              "Delivered $",
              "Progress",
            ].map((h) => (
              <th
                key={h}
                className="px-2 py-2 text-left text-[10px] font-semibold text-gray-400 uppercase whitespace-nowrap"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-700/50">
          {items.map((item, i) => {
            const netQty = item.requiredQty || item.quantity || 0;
            // Coverage handling: delta-row qty is in *units* (e.g. boxes); the
            // parent budget row's netQty is in coverage UOM (LF/SF). For SF/LF
            // items we multiply by coveragePerUnit so they're comparable.
            const isCov = isCoverageUOM(item.uom);
            const rawCov = parseFloat(item.coveragePerUnit);
            const cov = isCov && rawCov && rawCov > 0 ? rawCov : 1;
            const qtyOrdered = (item.qtyOrdered || 0) * cov;
            const qtyDelivered = (item.qtyDelivered || 0) * cov;
            const qtyPending = (item.qtyPending || 0) * cov;
            const remaining = Math.max(0, netQty - qtyDelivered - qtyPending);
            const unitCost = item.unitCost || item.price || 0;
            const budget = item.amount || 0;
            const deliveredAmt = item.amountDelivered || 0;
            const deliveredPct = pct(qtyDelivered, netQty);
            const pendingPct2 = pct(qtyPending, netQty);
            const isCO = item.source === "change_order";

            return (
              <tr
                key={item._id || i}
                className={cls(
                  "hover:bg-gray-700/30",
                  isCO && "border-l-2 border-purple-500/40",
                )}
              >
                <td
                  className="px-2 py-2 text-gray-200 max-w-[260px] truncate"
                  title={item.costCodeDescription || item.itemDescription}
                >
                  {item.costCodeDescription || item.itemDescription || "—"}
                  {isCO && (
                    <span className="ml-1 text-[9px] bg-purple-600/60 text-purple-100 px-1 py-0.5 rounded font-semibold">
                      CO
                    </span>
                  )}
                </td>
                <td className="px-2 py-2 text-gray-300">{item.size || "—"}</td>
                <td className="px-2 py-2 text-right text-gray-200 font-medium">
                  {fmtQty(netQty)}
                </td>
                <td className="px-2 py-2 text-gray-400">{item.uom || "—"}</td>
                <td className="px-2 py-2 text-right text-gray-300">
                  {qtyOrdered > 0 ? fmtQty(qtyOrdered) : "—"}
                </td>
                <td className="px-2 py-2 text-right text-green-400 font-medium">
                  {qtyDelivered > 0 ? fmtQty(qtyDelivered) : "—"}
                </td>
                <td className="px-2 py-2 text-right text-yellow-400">
                  {qtyPending > 0 ? fmtQty(qtyPending) : "—"}
                </td>
                <td className="px-2 py-2 text-right">
                  {remaining > 0 ? (
                    <span className="text-orange-300">{fmtQty(remaining)}</span>
                  ) : (
                    <span className="text-green-400 font-bold">✓</span>
                  )}
                </td>
                <td className="px-2 py-2 text-right text-gray-300">
                  {fmtExact(unitCost)}
                </td>
                <td className="px-2 py-2 text-right text-gray-200">
                  {fmt(budget)}
                </td>
                <td className="px-2 py-2 text-right text-green-400">
                  {deliveredAmt > 0 ? fmt(deliveredAmt) : "—"}
                </td>
                <td className="px-2 py-2">
                  <div className="flex items-center gap-2">
                    <div className="w-20 bg-gray-700 rounded-full h-1.5 overflow-hidden flex">
                      <div
                        className="h-full bg-green-500"
                        style={{ width: `${Math.min(100, deliveredPct)}%` }}
                      />
                      <div
                        className="h-full bg-yellow-400/80"
                        style={{
                          width: `${Math.min(100 - deliveredPct, pendingPct2)}%`,
                        }}
                      />
                    </div>
                    <span
                      className={`text-[10px] font-semibold ${deliveredPct >= 90 ? "text-green-400" : deliveredPct >= 50 ? "text-blue-400" : "text-gray-400"}`}
                    >
                      {deliveredPct.toFixed(0)}%
                    </span>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// DRILL-DOWNS — Piecework / Hourly / Subcontractor / Materials
// Each shows: KPI strip at top, then a list of <SectionAccordionRow>s.
// ════════════════════════════════════════════════════════════════════════════

// Per-item rollup function for piecework sections (charged/pending/installed)
const rollupPieceworkItem = (item) => {
  const isBudget = item.source === "budget";
  const isCONew = item.source === "change_order";
  const origQty = item.requiredQty || item.quantity || 0;
  const netQty = isBudget
    ? origQty + (item.totalCODeltaQty || 0)
    : isCONew
      ? item.totalCODeltaQty || 0
      : item.qtyInstalled || 0;
  const budget = isBudget
    ? (item.amount || 0) + (item.totalCOAmount || 0)
    : isCONew
      ? item.totalCOAmount || 0
      : 0;
  return {
    budget,
    charged: item.amountActual || 0,
    pending: item.amountPending || 0,
    qty: netQty,
    installed: item.qtyInstalled || 0,
    pendingQty: item.qtyPending || 0,
  };
};

// Per-item rollup for budget-only sections (hourly / subcontractor)
const rollupBudgetItem = (item) => {
  const isBudget = item.source === "budget";
  const origQty = item.requiredQty || item.quantity || 0;
  const netQty = isBudget
    ? origQty + (item.totalCODeltaQty || 0)
    : item.totalCODeltaQty || 0;
  const budget = isBudget
    ? (item.amount || 0) + (item.totalCOAmount || 0)
    : item.totalCOAmount || 0;
  return { budget, charged: 0, pending: 0, qty: netQty };
};

// Per-item rollup for materials — coverage-aware on qty
const rollupMaterialItem = (item) => {
  const isCov = isCoverageUOM(item.uom);
  const rawCov = parseFloat(item.coveragePerUnit);
  const cov = isCov && rawCov && rawCov > 0 ? rawCov : 1;
  const netQty = item.requiredQty || item.quantity || 0;
  return {
    budget: item.amount || 0,
    charged: item.amountDelivered || 0,
    pending: Math.max(
      0,
      (item.amountOrdered || 0) - (item.amountDelivered || 0),
    ),
    qty: netQty,
    installed: (item.qtyDelivered || 0) * cov,
    pendingQty: (item.qtyPending || 0) * cov,
  };
};

// ─── Sub agreement breakdown (per WO) ───────────────────────────────────────
// completed / paid → Cost Incurred   ·   issued → Committed   (pre-tax subtotal)
const SA_STATUS = {
  issued: { label: "Issued", cls: "bg-yellow-700/60 text-yellow-100" },
  completed: { label: "Completed", cls: "bg-green-700/60 text-green-100" },
  paid: { label: "Paid", cls: "bg-blue-700/60 text-blue-100" },
};

function SubAgreementBreakdown({ woId, token, woSummary, onChanged }) {
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [closedSet, setClosedSet] = useState(() => new Set());
  const [viewFor, setViewFor] = useState(null);

  const count =
    (woSummary?.subAgreementActualCount || 0) +
    (woSummary?.subAgreementPendingCount || 0);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(
        `${API_BASE}/subagreements3/work-order/${woId}`,
        { headers },
      );
      setAgreements(r.data.agreements || []);
      setErr("");
    } catch (e) {
      setErr(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, [woId, headers]);

  useEffect(() => {
    if (count > 0) load();
    else setLoading(false);
  }, [count, load]);

  const groups = useMemo(() => {
    const map = new Map();
    agreements.forEach((a) => {
      const key = String(a.subContractor?._id || "none");
      if (!map.has(key))
        map.set(key, {
          key,
          name:
            a.subInfo?.businessName ||
            a.subContractor?.displayName ||
            "No subcontractor",
          rows: [],
          actual: 0,
          committed: 0,
        });
      const g = map.get(key);
      g.rows.push(a);
      if (a.status === "issued") g.committed += a.subtotal || 0;
      else g.actual += a.subtotal || 0;
    });
    return [...map.values()]
      .map((g) => ({
        ...g,
        rows: g.rows.sort(
          (x, y) => new Date(y.createdAt) - new Date(x.createdAt),
        ),
      }))
      .sort((a, b) => b.actual + b.committed - (a.actual + a.committed));
  }, [agreements]);

  const totals = useMemo(
    () =>
      agreements.reduce(
        (t, a) => {
          const v = a.subtotal || 0;
          if (a.status === "issued") t.committed += v;
          else t.actual += v;
          if (a.status === "paid") t.paid += v;
          return t;
        },
        { actual: 0, committed: 0, paid: 0 },
      ),
    [agreements],
  );

  if (count === 0) return null;

  const toggle = (k) =>
    setClosedSet((prev) => {
      const next = new Set(prev);
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });

  const handleChanged = () => {
    load();
    onChanged?.();
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-2 flex-wrap">
        <div className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold">
          Sub Agreements — {agreements.length || count} · {groups.length || "…"}{" "}
          subcontractor{groups.length !== 1 ? "s" : ""}
        </div>
        {loading && (
          <span className="text-[10px] text-gray-600 animate-pulse">
            Loading…
          </span>
        )}
        <div className="ml-auto flex items-center gap-4 text-[11px] text-gray-400">
          <span>
            Incurred{" "}
            <span className="text-green-400 font-semibold font-mono">
              {fmtExact(totals.actual)}
            </span>
          </span>
          <span>
            Committed{" "}
            <span className="text-yellow-400 font-semibold font-mono">
              {fmtExact(totals.committed)}
            </span>
          </span>
          {totals.paid > 0 && (
            <span>
              Paid{" "}
              <span className="text-blue-400 font-semibold font-mono">
                {fmtExact(totals.paid)}
              </span>
            </span>
          )}
        </div>
      </div>

      {err ? (
        <p className="text-xs text-red-400">{err}</p>
      ) : (
        <div className="space-y-2">
          {groups.map((g) => {
            const open = !closedSet.has(g.key);
            return (
              <div
                key={g.key}
                className={cls(
                  "rounded-md border transition-colors",
                  open
                    ? "border-rose-500/40 bg-gray-900/40"
                    : "border-gray-700/70 bg-gray-900/20 hover:border-gray-600",
                )}
              >
                <button
                  type="button"
                  onClick={() => toggle(g.key)}
                  className="w-full px-3 py-2.5 flex items-center gap-3 text-left"
                >
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded shrink-0 bg-rose-900/60 text-rose-300 border border-rose-700/40">
                    SA
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-200 truncate">
                        {g.name}
                      </span>
                      <span className="text-[10px] text-gray-500 shrink-0">
                        {g.rows.length} agreement
                        {g.rows.length !== 1 ? "s" : ""}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      <span className="text-[11px] text-gray-400">
                        Incurred{" "}
                        <span className="text-green-400 font-medium">
                          {fmt(g.actual)}
                        </span>
                      </span>
                      {g.committed > 0 && (
                        <span className="text-[11px] text-gray-400">
                          Committed{" "}
                          <span className="text-yellow-400 font-medium">
                            {fmt(g.committed)}
                          </span>
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="w-28 shrink-0">
                    <CostBar
                      budget={g.actual + g.committed}
                      charged={g.actual}
                      pending={g.committed}
                      height="h-1.5"
                    />
                  </div>
                  <svg
                    className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {open && (
                  <div className="border-t border-gray-700/50 px-3 pt-2 pb-3">
                    <div className="overflow-x-auto rounded border border-gray-700">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-900/80">
                          <tr>
                            {[
                              "Agreement",
                              "Title",
                              "Scheduled",
                              "Status",
                              "Signed",
                              "Subtotal (cost)",
                              "Tax",
                              "Total",
                              "Counted as",
                            ].map((h, i) => (
                              <th
                                key={h}
                                className={cls(
                                  "px-2 py-2 text-[10px] font-semibold text-gray-400 uppercase whitespace-nowrap",
                                  i >= 5 && i <= 7 ? "text-right" : "text-left",
                                )}
                              >
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700/50">
                          {g.rows.map((a) => {
                            const st = SA_STATUS[a.status] || {
                              label: a.status,
                              cls: "bg-gray-700 text-gray-300",
                            };
                            const isIssued = a.status === "issued";
                            return (
                              <tr
                                key={a._id}
                                onClick={() => setViewFor(a)}
                                className={cls(
                                  "cursor-pointer hover:bg-gray-700/30",
                                  isIssued && "border-l-2 border-yellow-500/50",
                                )}
                              >
                                <td className="px-2 py-2 font-mono text-orange-400 whitespace-nowrap">
                                  {a.agreementNumber || "—"}
                                </td>
                                <td
                                  className="px-2 py-2 text-gray-200 max-w-[220px] truncate"
                                  title={a.title}
                                >
                                  {a.title || "—"}
                                </td>
                                <td className="px-2 py-2 text-gray-400 whitespace-nowrap">
                                  {a.scheduledDate
                                    ? new Date(
                                        a.scheduledDate,
                                      ).toLocaleDateString("en-CA", {
                                        timeZone: "UTC",
                                      })
                                    : "—"}
                                </td>
                                <td className="px-2 py-2">
                                  <span
                                    className={cls(
                                      "px-1.5 py-0.5 text-[9px] font-semibold rounded uppercase",
                                      st.cls,
                                    )}
                                  >
                                    {st.label}
                                  </span>
                                </td>
                                <td className="px-2 py-2 text-gray-400">
                                  {a.signedAt ? "🔒 Signed" : "—"}
                                </td>
                                <td
                                  className={cls(
                                    "px-2 py-2 text-right font-mono font-semibold",
                                    isIssued
                                      ? "text-yellow-300"
                                      : "text-green-300",
                                  )}
                                >
                                  {fmtExact(a.subtotal)}
                                </td>
                                <td className="px-2 py-2 text-right text-gray-400 font-mono">
                                  {fmtExact(a.tax)}
                                </td>
                                <td className="px-2 py-2 text-right text-gray-300 font-mono">
                                  {fmtExact(a.total)}
                                </td>
                                <td className="px-2 py-2 text-[10px] whitespace-nowrap">
                                  {isIssued ? (
                                    <span className="text-yellow-400">
                                      Committed
                                    </span>
                                  ) : (
                                    <span className="text-green-400">
                                      Cost incurred
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot className="bg-gray-900/80 border-t border-gray-600">
                          <tr>
                            <td
                              colSpan={5}
                              className="px-2 py-2 text-[11px] text-gray-400"
                            >
                              {g.rows.length} agreement
                              {g.rows.length !== 1 ? "s" : ""} · costs use
                              pre-tax subtotal
                            </td>
                            <td className="px-2 py-2 text-right text-sm font-bold text-white font-mono">
                              {fmtExact(g.actual + g.committed)}
                            </td>
                            <td colSpan={3} />
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!loading && !err && agreements.length === 0 && (
        <p className="text-xs text-gray-500">
          The server returned no agreements for this work order, but the WIP
          rollup counts {count}.
        </p>
      )}

      {viewFor && (
        <SubAgreementModal
          key={viewFor._id}
          agreement={viewFor}
          headers={headers}
          onClose={() => setViewFor(null)}
          onChanged={handleChanged}
        />
      )}
    </div>
  );
}
// ─── Piecework drill-down ───────────────────────────────────────────────────
// ─── Piecework drill-down ───────────────────────────────────────────────────
function PieceworkDrillDown({ woId, token, woSummary, onAgreementChanged }) {
  const { result, loading } = useWOItems(woId, token);
  const { resolveName } = useCostCodeMap();

  const items = useMemo(
    () =>
      (result?.items || []).filter(
        (i) => i.source !== "piecework_unbudgeted" || (i.qtyInstalled || 0) > 0,
      ),
    [result],
  );

  const sections = useMemo(
    () => groupItemsBySection(items, rollupPieceworkItem, resolveName),
    [items, resolveName],
  );

  if (loading)
    return (
      <div className="py-6 text-center text-gray-400 text-sm">
        Loading items…
      </div>
    );
  if (!result) return null;

  const logCount = new Set(
    Object.values(result.logHistory || {})
      .flat()
      .map((l) => l.logId?.toString())
      .filter(Boolean),
  ).size;

  const saActual = woSummary?.subAgreementActual || 0;
  const saPending = woSummary?.subAgreementPending || 0;

  const totals = sections.reduce(
    (acc, s) => ({
      budget: acc.budget + s.budget,
      charged: acc.charged + s.charged,
      pending: acc.pending + s.pending,
      installed: acc.installed + s.installed,
      pendingQty: acc.pendingQty + s.pendingQty,
    }),
    { budget: 0, charged: 0, pending: 0, installed: 0, pendingQty: 0 },
  );
  const charged = totals.charged + saActual;
  const pending = totals.pending + saPending;

  return (
    <div className="mt-3">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 bg-gray-900/60 rounded-lg p-3 border border-gray-700 mb-3">
        <div>
          <div className="text-[10px] text-gray-400 uppercase tracking-widest mb-1">
            Budget
          </div>
          <div className="text-base font-bold text-gray-200">
            {fmt(totals.budget)}
          </div>
        </div>
        <div>
          <div className="text-[10px] text-gray-400 uppercase tracking-widest mb-1">
            Cost Incurred
          </div>
          <div className="text-base font-bold text-green-400">
            {fmt(charged)}
          </div>
          <div className="text-[10px] text-gray-400">
            {fmtQty(totals.installed)} qty
            {saActual > 0 && ` · incl. ${fmt(saActual)} agreements`}
          </div>
        </div>
        <div>
          <div className="text-[10px] text-gray-400 uppercase tracking-widest mb-1">
            Committed Costs
          </div>
          <div className="text-base font-bold text-yellow-400">
            {fmt(pending)}
          </div>
          <div className="text-[10px] text-gray-400">
            {fmtQty(totals.pendingQty)} qty
            {saPending > 0 && ` · incl. ${fmt(saPending)} agreements`}
          </div>
        </div>
        <div>
          <div className="text-[10px] text-gray-400 uppercase tracking-widest mb-1">
            Remaining
          </div>
          <div className="text-base font-bold text-orange-300">
            {fmt(Math.max(0, totals.budget - charged - pending))}
          </div>
        </div>
        <div>
          <div className="text-[10px] text-gray-400 uppercase tracking-widest mb-1">
            Logs / Sections
          </div>
          <div className="text-base font-bold text-blue-400">
            {logCount} <span className="text-gray-500 text-sm">/</span>{" "}
            {sections.length}
          </div>
          <div className="text-[10px] text-gray-400">{items.length} items</div>
        </div>
      </div>

      <div className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold mb-2">
        Sections — click to expand line items
      </div>

      <SectionList
        sections={sections}
        renderItems={(items) => <PieceworkItemsTable items={items} />}
        keyPrefix={`pw-${woId}`}
      />
    </div>
  );
}

// ─── Hourly drill-down ──────────────────────────────────────────────────────
function HourlyDrillDown({ woId, token, woSummary }) {
  const { result, loading } = useWOItems(woId, token);
  const { resolveName } = useCostCodeMap();

  const items = useMemo(
    () =>
      (result?.items || []).filter((i) => i.source !== "piecework_unbudgeted"),
    [result],
  );

  const sections = useMemo(
    () => groupItemsBySection(items, rollupBudgetItem, resolveName),
    [items, resolveName],
  );

  if (loading)
    return (
      <div className="py-6 text-center text-gray-400 text-sm">Loading…</div>
    );
  if (!result) return null;

  const ts = result.timesheetSummary;
  const saActual = woSummary?.subAgreementActual || 0;
  const saPending = woSummary?.subAgreementPending || 0;
  const incurred = (ts?.approved?.totalCost || 0) + saActual;
  const committed = (ts?.pending?.totalCost || 0) + saPending;
  const showCommitted = (ts?.pending?.count || 0) > 0 || saPending > 0;

  return (
    <div className="mt-3 space-y-4">
      {(ts || saActual > 0 || saPending > 0) && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 bg-gray-900/60 rounded-lg p-3 border border-gray-700">
          <div>
            <div className="text-[10px] text-gray-400 uppercase tracking-widest mb-1">
              Cost Incurred (Approved)
            </div>
            <div className="text-base font-bold text-orange-400">
              {fmtExact(incurred)}
            </div>
            <div className="text-[10px] text-gray-400">
              {ts?.approved?.hours || 0}h · {ts?.approved?.count || 0} sheets
              {saActual > 0 && ` · + ${fmtExact(saActual)} agreements`}
            </div>
          </div>
          {showCommitted && (
            <div>
              <div className="text-[10px] text-gray-400 uppercase tracking-widest mb-1">
                Committed Costs (Pending)
              </div>
              <div className="text-base font-bold text-yellow-400">
                {fmtExact(committed)}
              </div>
              <div className="text-[10px] text-gray-400">
                {ts?.pending?.hours || 0}h · {ts?.pending?.count || 0} sheets
                {saPending > 0 && ` · + ${fmtExact(saPending)} agreements`}
              </div>
            </div>
          )}
          <div>
            <div className="text-[10px] text-gray-400 uppercase tracking-widest mb-1">
              Budget
            </div>
            <div className="text-base font-bold text-gray-200">
              {fmt(woSummary?.budgetAmt || 0)}
            </div>
            <div className="text-[10px] text-gray-400">
              {items.length} budget items · {sections.length} sections
            </div>
          </div>
          {(ts?.approved?.burdenCost || 0) > 0 && (
            <div>
              <div className="text-[10px] text-gray-400 uppercase tracking-widest mb-1">
                Breakdown
              </div>
              <div className="text-[10px] text-gray-400 space-y-0.5">
                <div>Base: {fmtExact(ts.approved.subtotal)}</div>
                <div>Burden: {fmtExact(ts.approved.burdenCost)}</div>
                {(ts.approved?.wsibCost || 0) > 0 && (
                  <div>WSIB: {fmtExact(ts.approved.wsibCost)}</div>
                )}
                {saActual > 0 && <div>Agreements: {fmtExact(saActual)}</div>}
              </div>
            </div>
          )}
        </div>
      )}

      <div>
        <div className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold mb-2">
          Budget Sections — click to expand line items
        </div>
        <SectionList
          sections={sections}
          renderItems={(items) => <BudgetItemsTable items={items} />}
          keyPrefix={`hr-${woId}`}
        />
      </div>
    </div>
  );
}

// ─── Subcontractor drill-down ───────────────────────────────────────────────
function SubcontractorDrillDown({ woId, token, woSummary }) {
  const { result, loading } = useWOItems(woId, token);
  const { resolveName } = useCostCodeMap();

  const items = useMemo(
    () =>
      (result?.items || []).filter((i) => i.source !== "piecework_unbudgeted"),
    [result],
  );
  const sections = useMemo(
    () => groupItemsBySection(items, rollupBudgetItem, resolveName),
    [items, resolveName],
  );

  if (loading)
    return (
      <div className="py-6 text-center text-gray-400 text-sm">Loading…</div>
    );
  if (!result) return null;

  // Bills + agreements merged
  const saActual = woSummary?.subAgreementActual || 0;
  const saPending = woSummary?.subAgreementPending || 0;
  const procAmt = (woSummary?.billProcessedSubtotal || 0) + saActual;
  const pendAmt = (woSummary?.billPendingSubtotal || 0) + saPending;
  const procCnt = woSummary?.billProcessedCount || 0;
  const pendCnt = woSummary?.billPendingCount || 0;
  const budget = woSummary?.budgetAmt || 0;
  const remaining = Math.max(0, budget - procAmt - pendAmt);
  const usedPct = budget > 0 ? Math.min(100, (procAmt / budget) * 100) : 0;

  return (
    <div className="mt-3 space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 bg-gray-900/60 rounded-lg p-3 border border-gray-700">
        <div>
          <div className="text-[10px] text-gray-400 uppercase tracking-widest mb-1">
            Budget
          </div>
          <div className="text-base font-bold text-gray-200">{fmt(budget)}</div>
          <div className="text-[10px] text-gray-400">
            {items.length} budget items · {sections.length} sections
          </div>
        </div>
        <div>
          <div className="text-[10px] text-gray-400 uppercase tracking-widest mb-1">
            Cost Incurred
          </div>
          <div
            className={`text-base font-bold ${procAmt > budget && budget > 0 ? "text-red-400" : "text-green-400"}`}
          >
            {fmtExact(procAmt)}
          </div>
          <div className="text-[10px] text-gray-400">
            {procCnt} processed bill{procCnt !== 1 ? "s" : ""}
            {saActual > 0 && ` + ${fmtExact(saActual)} agreements`} ·{" "}
            {usedPct.toFixed(1)}% of budget
          </div>
        </div>
        {pendAmt > 0 && (
          <div>
            <div className="text-[10px] text-gray-400 uppercase tracking-widest mb-1">
              Committed (Pending)
            </div>
            <div className="text-base font-bold text-yellow-400">
              {fmtExact(pendAmt)}
            </div>
            <div className="text-[10px] text-gray-400">
              {pendCnt} bill{pendCnt !== 1 ? "s" : ""} awaiting processing
              {saPending > 0 && ` · ${fmtExact(saPending)} issued agreements`}
            </div>
          </div>
        )}
        <div>
          <div className="text-[10px] text-gray-400 uppercase tracking-widest mb-1">
            Remaining
          </div>
          <div className="text-base font-bold text-orange-300">
            {fmt(remaining)}
          </div>
        </div>
      </div>

      <div>
        <div className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold mb-2">
          Budget Sections — {sections.length} · {items.length} items
        </div>
        <SectionList
          sections={sections}
          renderItems={(items) => <BudgetItemsTable items={items} />}
          keyPrefix={`sc-${woId}`}
        />
      </div>
    </div>
  );
}

// ─── Surcharges (MO surcharges / credits) breakdown for a PO ────────────────
function SurchargeBreakdown({ entries = [], total = 0 }) {
  if (!entries.length) return null;
  return (
    <div className="mt-4">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold">
          Material Order Surcharges — {entries.length}
        </div>
        <span
          className={cls(
            "text-xs font-semibold font-mono",
            total < 0 ? "text-green-400" : "text-amber-300",
          )}
        >
          {fmtExact(total)}
        </span>
      </div>
      <div className="overflow-x-auto rounded border border-gray-700">
        <table className="w-full text-xs">
          <thead className="bg-gray-900/80">
            <tr>
              {["MO #", "Description", "MO Status", "Counted as", "Amount"].map(
                (h, i) => (
                  <th
                    key={h}
                    className={cls(
                      "px-2 py-2 text-[10px] font-semibold text-gray-400 uppercase",
                      i === 4 ? "text-right" : "text-left",
                    )}
                  >
                    {h}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700/50">
            {entries.map((e, i) => {
              const delivered = e.status === "delivered";
              return (
                <tr key={`${e.orderId}-${i}`} className="hover:bg-gray-700/30">
                  <td className="px-2 py-2 font-mono text-orange-400">
                    MO #{e.orderNumber ?? "—"}
                  </td>
                  <td className="px-2 py-2 text-gray-200">{e.description}</td>
                  <td className="px-2 py-2 text-gray-400 capitalize">
                    {e.status || "—"}
                  </td>
                  <td className="px-2 py-2 text-[10px] whitespace-nowrap">
                    {delivered ? (
                      <span className="text-green-400">Cost incurred</span>
                    ) : (
                      <span className="text-yellow-400">Committed</span>
                    )}
                  </td>
                  <td
                    className={cls(
                      "px-2 py-2 text-right font-mono font-semibold",
                      e.amount < 0 ? "text-green-400" : "text-amber-300",
                    )}
                  >
                    {fmtExact(e.amount)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Material PO drill-down ─────────────────────────────────────────────────
function MaterialDrillDown({ poId, token, po }) {
  const { result, loading } = usePOItems(poId, token);
  const { resolveName } = useCostCodeMap();

  const items = useMemo(() => result?.items || [], [result]);

  const sections = useMemo(
    () => groupItemsBySection(items, rollupMaterialItem, resolveName),
    [items, resolveName],
  );

  if (loading)
    return (
      <div className="py-6 text-center text-gray-400 text-sm">Loading…</div>
    );
  if (!result) return null;

  return (
    <div className="mt-3">
      <div className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold mb-2">
        Sections — {sections.length} · {items.length} items total
      </div>
      <SectionList
        sections={sections}
        renderItems={(items) => <MaterialItemsTable items={items} />}
        keyPrefix={`po-${poId}`}
      />
      <SurchargeBreakdown
        entries={po?.surcharges || []}
        total={po?.surchargeAmt || 0}
      />
    </div>
  );
}

// ─── WO accordion header ────────────────────────────────────────────────────
function WOAccordionHeader({ wo }) {
  const { budgetAmt, chargedAmt, pendingAmt, remainingAmt } = wo;
  const overBudget = chargedAmt > budgetAmt && budgetAmt > 0;
  const chargedPct = pct(chargedAmt, budgetAmt);
  return (
    <div className="flex-1 flex items-center gap-3 min-w-0 pr-4">
      <TypeBadge type={wo.type} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-white truncate">
            {wo.title}
          </span>
          <span className="text-xs text-gray-400">WO#{wo.woNumber}</span>
          {wo.union && wo.union !== "non-union" && (
            <span className="text-[10px] px-1.5 py-0.5 bg-teal-900/60 text-teal-300 rounded">
              {wo.union}
            </span>
          )}
          {wo.bidArea && (
            <span className="text-[10px] text-gray-500">{wo.bidArea}</span>
          )}
          {(wo.subAgreementActualCount || 0) +
            (wo.subAgreementPendingCount || 0) >
            0 && (
            <span className="text-[10px] px-1.5 py-0.5 bg-rose-900/60 text-rose-300 rounded">
              {(wo.subAgreementActualCount || 0) +
                (wo.subAgreementPendingCount || 0)}{" "}
              agreement
              {(wo.subAgreementActualCount || 0) +
                (wo.subAgreementPendingCount || 0) !==
              1
                ? "s"
                : ""}
            </span>
          )}
        </div>
        <div className="flex items-center gap-4 mt-1 flex-wrap">
          <span className="text-[11px] text-gray-400">
            Budget{" "}
            <span className="text-gray-200 font-medium">{fmt(budgetAmt)}</span>
          </span>
          <span className="text-[11px] text-gray-400">
            Cost Incurred{" "}
            <span
              className={`font-medium ${overBudget ? "text-red-400" : "text-green-400"}`}
            >
              {fmt(chargedAmt)}
            </span>
          </span>
          {pendingAmt > 0 && (
            <span className="text-[11px] text-gray-400">
              Committed{" "}
              <span className="text-yellow-400 font-medium">
                {fmt(pendingAmt)}
              </span>
            </span>
          )}
          <span className="text-[11px] text-gray-400">
            Remaining{" "}
            <span className="text-orange-300 font-medium">
              {fmt(remainingAmt)}
            </span>
          </span>
        </div>
      </div>
      <div className="w-32 shrink-0">
        <div className="flex justify-between text-[10px] text-gray-400 mb-1">
          <span>{chargedPct.toFixed(0)}% incurred</span>
        </div>
        <CostBar
          budget={budgetAmt}
          charged={chargedAmt}
          pending={pendingAmt}
          height="h-1.5"
        />
      </div>
    </div>
  );
}

// ─── PO accordion header ────────────────────────────────────────────────────
function POAccordionHeader({ po }) {
  const { budgetAmt, orderedAmt, pendingAmt, remainingAmt } = po;
  const overBudget = orderedAmt > budgetAmt && budgetAmt > 0;
  const orderedPct = pct(orderedAmt, budgetAmt);
  return (
    <div className="flex-1 flex items-center gap-3 min-w-0 pr-4">
      <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border bg-orange-700 text-orange-200 border-orange-500">
        PO
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-white truncate">
            {po.title}
          </span>
          <span className="text-xs text-gray-400">PO#{po.poNumber}</span>
          {po.vendor && (
            <span className="text-[10px] px-1.5 py-0.5 bg-gray-700 text-gray-400 rounded truncate max-w-[120px]">
              {po.vendor}
            </span>
          )}
          {po.bidArea && (
            <span className="text-[10px] text-gray-500">{po.bidArea}</span>
          )}
        </div>
        <div className="flex items-center gap-4 mt-1 flex-wrap">
          <span className="text-[11px] text-gray-400">
            Budget{" "}
            <span className="text-gray-200 font-medium">{fmt(budgetAmt)}</span>
          </span>
          <span className="text-[11px] text-gray-400">
            Delivered{" "}
            <span
              className={`font-medium ${overBudget ? "text-red-400" : "text-green-400"}`}
            >
              {fmt(orderedAmt)}
            </span>
            {po.surchargeDelivered !== 0 && (
              <span className="text-gray-500">
                {" "}
                (incl. {fmt(po.surchargeDelivered)} surcharges)
              </span>
            )}
          </span>
          {pendingAmt > 0 && (
            <span className="text-[11px] text-gray-400">
              Pending{" "}
              <span className="text-yellow-400 font-medium">
                {fmt(pendingAmt)}
              </span>
              {po.surchargePending !== 0 && (
                <span className="text-gray-500">
                  {" "}
                  (incl. {fmt(po.surchargePending)} surcharges)
                </span>
              )}
            </span>
          )}
          <span className="text-[11px] text-gray-400">
            Remaining{" "}
            <span className="text-orange-300 font-medium">
              {fmt(remainingAmt)}
            </span>
          </span>
        </div>
      </div>
      <div className="w-32 shrink-0">
        <div className="flex justify-between text-[10px] text-gray-400 mb-1">
          <span>{orderedPct.toFixed(0)}% delivered</span>
        </div>
        <CostBar
          budget={budgetAmt}
          charged={orderedAmt}
          pending={pendingAmt}
          height="h-1.5"
        />
      </div>
    </div>
  );
}

// Drill-down body for any WO type, with agreements always appended
// Drill-down body for any WO type, with agreements always appended
function WODrillDownBody({ wo, token, onAgreementChanged }) {
  const isPiecework = wo.type === "piecework" || wo.type === "union_piecework";
  const isHourly = wo.type === "hourly" || wo.type === "union_hourly";
  const Body = isPiecework
    ? PieceworkDrillDown
    : isHourly
      ? HourlyDrillDown
      : SubcontractorDrillDown;
  return (
    <>
      <Body woId={wo._id} token={token} woSummary={wo} />
      <div className="mt-4">
        <SubAgreementBreakdown
          woId={wo._id}
          token={token}
          woSummary={wo}
          onChanged={onAgreementChanged}
        />
      </div>
    </>
  );
}

// One labour group (Piecework / Hourly / Subcontractor / Other)
function LabourGroup({
  title,
  icon,
  prefix,
  wos,
  totals,
  openAccordions,
  toggleAccordion,
  token,
  onAgreementChanged,
}) {
  if (wos.length === 0) return null;
  return (
    <div>
      <SectionHeader title={title} icon={icon} count={wos.length} {...totals} />
      <div className="space-y-2">
        {wos.map((wo) => {
          const key = `${prefix}-${wo._id}`;
          return (
            <AccordionCard
              key={key}
              isOpen={!!openAccordions[key]}
              onToggle={() => toggleAccordion(key)}
              header={<WOAccordionHeader wo={wo} />}
            >
              {openAccordions[key] && (
                <WODrillDownBody
                  wo={wo}
                  token={token}
                  onAgreementChanged={onAgreementChanged}
                />
              )}
            </AccordionCard>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
export default function WIPTab3({ project }) {
  const { id: projectIdFromUrl } = useParams();
  const projectId = project?._id || projectIdFromUrl;
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [reloadKey, setReloadKey] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openAccordions, setOpenAccordions] = useState({});
  const [activeTab, setActiveTab] = useState("labour");
  const [coBreakdownOpen, setCoBreakdownOpen] = useState(false);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    const load = async () => {
      if (reloadKey === 0) setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API_BASE}/wip3/project/${projectId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) {
          if (res.data.success) setData(res.data);
          else setError(res.data.message || "Failed to load WIP");
        }
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.message || err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [projectId, token, reloadKey]);

  const toggleAccordion = useCallback((key) => {
    setOpenAccordions((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);
  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);
  const { pieceworkWOs, hourlyWOs, subcontractorWOs, otherWOs } =
    useMemo(() => {
      const all = data?.workOrders || [];
      return {
        pieceworkWOs: all.filter(
          (w) => w.type === "piecework" || w.type === "union_piecework",
        ),
        hourlyWOs: all.filter(
          (w) => w.type === "hourly" || w.type === "union_hourly",
        ),
        subcontractorWOs: all.filter((w) => w.type === "subcontractor"),
        otherWOs: all.filter(
          (w) =>
            ![
              "piecework",
              "union_piecework",
              "hourly",
              "union_hourly",
              "subcontractor",
            ].includes(w.type),
        ),
      };
    }, [data]);

  const sumGroup = (arr) => {
    const totals = arr.reduce(
      (acc, w) => ({
        budget: acc.budget + (w.budgetAmt || 0),
        charged: acc.charged + (w.chargedAmt || 0),
        pending: acc.pending + (w.pendingAmt || 0),
      }),
      { budget: 0, charged: 0, pending: 0 },
    );
    return {
      ...totals,
      remaining: Math.max(0, totals.budget - totals.charged - totals.pending),
    };
  };

  if (loading)
    return (
      <div className="bg-gray-900 rounded-xl p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-400 text-sm">Loading WIP data…</div>
        </div>
      </div>
    );
  if (error)
    return (
      <div className="bg-red-950 border border-red-700/40 rounded-xl p-4 text-center text-red-300">
        {error}
      </div>
    );
  if (!data) return null;

  const r = data.rollup;
  const overallPct = pct(r.totalCharged, r.totalBudget);
  const pos = data.purchaseOrders || [];
  const labourWOs = [
    ...pieceworkWOs,
    ...hourlyWOs,
    ...subcontractorWOs,
    ...otherWOs,
  ];

  const labourTotals = sumGroup(labourWOs);
  const matRaw = pos.reduce(
    (acc, p) => ({
      budget: acc.budget + p.budgetAmt,
      ordered: acc.ordered + p.orderedAmt,
      pending: acc.pending + p.pendingAmt,
    }),
    { budget: 0, ordered: 0, pending: 0 },
  );
  const matTotals = {
    ...matRaw,
    remaining: Math.max(0, matRaw.budget - matRaw.ordered - matRaw.pending),
  };

  const approvedCOs = (data.changeOrders || []).filter(
    (co) => co.status === "Approved" && co.billable === true,
  );
  const pendingCOs = (data.changeOrders || []).filter(
    (co) => co.status === "Pending" && co.billable === true,
  );
  const rejectedCOs = (data.changeOrders || []).filter(
    (co) => co.status === "Rejected" || co.status === "Reverted",
  );

  return (
    <div className="bg-gray-900 rounded-xl p-1">
      <div className="max-w-full mx-auto px-4 space-y-6">
        {/* ── Header ── */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white tracking-tight">
              Work In Progress
            </h1>
            <p className="text-sm text-gray-400 mt-0.5">
              Project cost tracking — charges, pending, and remaining budget
            </p>
          </div>
        </div>

        {/* ── Top summary strip ── */}
        <div className="bg-gray-800 rounded-xl border border-gray-600 overflow-hidden shadow-xl">
          <div className="px-6 py-4 border-b border-gray-700">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
              <SummaryTile
                label="Total Estimated Project Cost"
                value={fmt(r.totalBudget)}
                color="text-white"
              />
              <SummaryTile
                label="Cost Incurred"
                value={fmt(r.totalCharged)}
                sub={
                  `${overallPct.toFixed(1)}% of budget` +
                  (r.totalSurchargesDelivered !== 0
                    ? ` · incl. ${fmt(r.totalSurchargesDelivered)} surcharges`
                    : "") +
                  (r.totalSubAgreementActual > 0
                    ? ` · incl. ${fmt(r.totalSubAgreementActual)} agreements`
                    : "")
                }
                color={
                  r.totalCharged > r.totalBudget
                    ? "text-red-400"
                    : "text-green-400"
                }
              />
              <SummaryTile
                label="Committed Costs"
                value={fmt(r.totalPending)}
                sub={
                  [
                    r.totalSurchargesPending !== 0
                      ? `incl. ${fmt(r.totalSurchargesPending)} surcharges`
                      : null,
                    r.totalSubAgreementPending > 0
                      ? `incl. ${fmt(r.totalSubAgreementPending)} agreements`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" · ") || undefined
                }
                color="text-yellow-400"
              />
              <SummaryTile
                label="Remaining"
                value={fmt(r.totalRemaining)}
                sub="Est. Cost − Incurred − Committed"
                color="text-orange-300"
              />
              {r.approvedCOValue + r.pendingCOValue > 0 && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold">
                    Change Orders
                  </span>
                  <span className="text-sm font-bold text-purple-400">
                    +{fmt(r.approvedCOValue)}
                  </span>
                  <span className="text-[10px] text-gray-400">
                    {r.approvedCOCount} approved
                  </span>
                  {r.pendingCOCount > 0 && (
                    <span className="text-[10px] text-yellow-500/80">
                      {r.pendingCOCount} pending · +{fmt(r.pendingCOValue)}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="px-6 py-4">
            <div className="flex items-center justify-between text-[11px] text-gray-400 mb-2">
              <span>Overall project cost progress</span>
              <span>
                {overallPct.toFixed(1)}% incurred
                {r.totalPending > 0 &&
                  ` · +${pct(r.totalPending, r.totalBudget).toFixed(1)}% committed`}
              </span>
            </div>
            <CostBar
              budget={r.totalBudget}
              charged={r.totalCharged}
              pending={r.totalPending}
              height="h-4"
            />
            <div className="flex items-center gap-5 mt-2">
              <span className="flex items-center gap-1.5 text-[11px] text-gray-400">
                <span className="w-2.5 h-2.5 rounded-sm bg-green-500 inline-block" />
                Cost Incurred
              </span>
              <span className="flex items-center gap-1.5 text-[11px] text-gray-400">
                <span className="w-2.5 h-2.5 rounded-sm bg-yellow-400/80 inline-block" />
                Committed Costs
              </span>
              <span className="flex items-center gap-1.5 text-[11px] text-gray-400">
                <span className="w-2.5 h-2.5 rounded-sm bg-gray-700 border border-gray-600 inline-block" />
                Remaining
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-700 border-t border-gray-700">
            <div className="px-6 py-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">
                  Labour
                </span>
                <span className="text-xs text-gray-400">
                  {labourWOs.length} work orders
                </span>
              </div>
              <div className="grid grid-cols-4 gap-3">
                <SummaryTile
                  label="Budget"
                  value={fmt(labourTotals.budget)}
                  small
                  color="text-gray-200"
                />
                <SummaryTile
                  label="Cost Incurred"
                  value={fmt(labourTotals.charged)}
                  small
                  color="text-green-400"
                />
                <SummaryTile
                  label="Committed"
                  value={fmt(labourTotals.pending)}
                  small
                  color="text-yellow-400"
                />
                <SummaryTile
                  label="Remaining"
                  value={fmt(labourTotals.remaining)}
                  small
                  color="text-orange-300"
                />
              </div>
              <div className="mt-2">
                <CostBar
                  budget={labourTotals.budget}
                  charged={labourTotals.charged}
                  pending={labourTotals.pending}
                  height="h-1.5"
                />
              </div>
            </div>
            <div className="px-6 py-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">
                  Materials
                </span>
                <span className="text-xs text-gray-400">
                  {pos.length} purchase orders
                </span>
              </div>
              <div className="grid grid-cols-4 gap-3">
                <SummaryTile
                  label="Budget"
                  value={fmt(matTotals.budget)}
                  small
                  color="text-gray-200"
                />
                <SummaryTile
                  label="Delivered"
                  value={fmt(matTotals.ordered)}
                  sub={
                    r.totalSurchargesDelivered !== 0
                      ? `incl. ${fmt(r.totalSurchargesDelivered)} surcharges`
                      : undefined
                  }
                  small
                  color="text-green-400"
                />
                <SummaryTile
                  label="Pending"
                  value={fmt(matTotals.pending)}
                  sub={
                    r.totalSurchargesPending !== 0
                      ? `incl. ${fmt(r.totalSurchargesPending)} surcharges`
                      : undefined
                  }
                  small
                  color="text-yellow-400"
                />
                <SummaryTile
                  label="Remaining"
                  value={fmt(matTotals.remaining)}
                  small
                  color="text-orange-300"
                />
              </div>
              <div className="mt-2">
                <CostBar
                  budget={matTotals.budget}
                  charged={matTotals.ordered}
                  pending={matTotals.pending}
                  height="h-1.5"
                />
              </div>
            </div>
          </div>
        </div>

        {/* ═════════════════════════════════════════════════════════════════
            Contract & Billing Overview
        ═════════════════════════════════════════════════════════════════ */}
        <div className="bg-gray-800 rounded-xl border border-gray-600 overflow-hidden shadow-xl">
          <div className="px-6 py-3 border-b border-gray-700 flex items-center gap-2">
            <span className="text-sm font-bold text-white tracking-tight">
              Contract &amp; Billing
            </span>
            <span className="text-[10px] text-gray-400 uppercase tracking-widest">
              Revenue overview
            </span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-gray-700 border-b border-gray-700">
            <div className="px-5 py-4">
              <div className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold mb-1">
                Original Contract
              </div>
              <div className="text-lg font-bold text-gray-200">
                {fmt(r.originalContractValue)}
              </div>
            </div>
            <div className="px-5 py-4">
              <div className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold mb-1">
                Approved CO Value
              </div>
              <div className="text-lg font-bold text-purple-400">
                {r.approvedCOValue > 0 ? `+${fmt(r.approvedCOValue)}` : fmt(0)}
              </div>
              <div className="text-[10px] text-gray-400">
                {r.approvedCOCount} change order
                {r.approvedCOCount !== 1 ? "s" : ""}
              </div>
            </div>
            <div className="px-5 py-4">
              <div className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold mb-1">
                Current Contract Value
              </div>
              <div className="text-lg font-bold text-white">
                {fmt(r.currentContractValue)}
              </div>
              <div className="text-[10px] text-gray-400">
                Original + approved COs
              </div>
            </div>
            <div className="px-5 py-4">
              <div className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold mb-1">
                Pending CO Value
              </div>
              <div className="text-lg font-bold text-yellow-400">
                {r.pendingCOValue > 0 ? `+${fmt(r.pendingCOValue)}` : "—"}
              </div>
              <div className="text-[10px] text-gray-400">
                {r.pendingCOCount} pending approval
              </div>
            </div>
          </div>

          {(data.changeOrders || []).length > 0 && (
            <div className="border-b border-gray-700">
              <button
                type="button"
                onClick={() => setCoBreakdownOpen((v) => !v)}
                className="w-full px-5 py-3 flex items-center justify-between hover:bg-gray-700/30 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold">
                    Change Order Breakdown
                  </span>
                  {!coBreakdownOpen && (
                    <div className="flex items-center gap-2">
                      {approvedCOs.length > 0 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border bg-green-900/40 text-green-400 border-green-700">
                          {approvedCOs.length} approved ·{" "}
                          {fmt(r.approvedCOValue)}
                        </span>
                      )}
                      {pendingCOs.length > 0 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border bg-yellow-900/30 text-yellow-400 border-yellow-700/50">
                          {pendingCOs.length} pending · {fmt(r.pendingCOValue)}
                        </span>
                      )}
                      {rejectedCOs.length > 0 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border bg-red-900/30 text-red-400 border-red-700/50">
                          {rejectedCOs.length} rejected/reverted
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <svg
                  className={`w-4 h-4 text-gray-400 shrink-0 transition-transform duration-200 ${coBreakdownOpen ? "rotate-180" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {coBreakdownOpen && (
                <div className="overflow-x-auto border-t border-gray-700">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-900/60">
                      <tr>
                        {[
                          "#",
                          "Title",
                          "Status",
                          "Billable",
                          "Customer Price",
                        ].map((h) => (
                          <th
                            key={h}
                            className="px-4 py-2 text-left text-[10px] font-semibold text-gray-400 uppercase whitespace-nowrap first:pl-5"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700/50">
                      {(data.changeOrders || []).map((co) => {
                        const isApproved = co.status === "Approved";
                        const isPending = co.status === "Pending";
                        return (
                          <tr key={co._id} className="hover:bg-gray-700/30">
                            <td className="px-4 py-2 pl-5 text-gray-400 font-medium whitespace-nowrap">
                              CO-
                              {String(co.changeOrderNumber || "").padStart(
                                3,
                                "0",
                              )}
                            </td>
                            <td
                              className="px-4 py-2 text-gray-200 max-w-[280px] truncate"
                              title={co.title}
                            >
                              {co.title}
                            </td>
                            <td className="px-4 py-2">
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border ${
                                  isApproved
                                    ? "bg-green-900/40 text-green-400 border-green-700"
                                    : isPending
                                      ? "bg-yellow-900/30 text-yellow-400 border-yellow-700/50"
                                      : "bg-red-900/30 text-red-400 border-red-700/50"
                                }`}
                              >
                                {co.status}
                              </span>
                            </td>
                            <td className="px-4 py-2">
                              {co.billingMode === "separate" ? (
                                <span className="text-[10px] text-purple-400">
                                  Separate
                                </span>
                              ) : co.billingMode === "nonBillable" ? (
                                <span className="text-[10px] text-gray-500">
                                  No
                                </span>
                              ) : (
                                <span className="text-[10px] text-green-400">
                                  Yes
                                </span>
                              )}
                            </td>
                            <td
                              className={`px-4 py-2 text-right font-semibold ${
                                isApproved
                                  ? "text-gray-200"
                                  : isPending
                                    ? "text-yellow-400"
                                    : "text-gray-500 line-through"
                              }`}
                            >
                              {fmt(co.totalCustomerPrice || co.totalPrice || 0)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-5 divide-y md:divide-y-0 md:divide-x divide-gray-700 border-b border-gray-700">
            <div className="px-5 py-4">
              <div className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold mb-1">
                Total Billed
              </div>
              <div className="text-lg font-bold text-green-400">
                {fmt(r.effectiveTotalBilled)}
              </div>
              <div className="text-[10px] text-gray-400">
                {r.billingPct.toFixed(1)}% of contract · {r.drawCount} draw
                {r.drawCount !== 1 ? "s" : ""}
              </div>
            </div>
            <div className="px-5 py-4">
              <div className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold mb-1">
                Holdback Retained
              </div>
              <div
                className={`text-lg font-bold ${r.netHoldbackRetained > 0 ? "text-purple-400" : "text-gray-400"}`}
              >
                {fmt(r.netHoldbackRetained)}
              </div>
              <div className="text-[10px] text-gray-400">
                {fmt(r.effectiveHoldbackDeducted)} deducted
                {r.totalHoldbackReleased > 0 &&
                  ` · ${fmt(r.totalHoldbackReleased)} released`}
              </div>
            </div>
            <div className="px-5 py-4">
              <div className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold mb-1">
                Total Paid
              </div>
              <div className="text-lg font-bold text-blue-400">
                {fmt(r.totalPaid)}
              </div>
              <div className="text-[10px] text-gray-400">
                {r.effectiveTotalBilled > 0
                  ? `${((r.totalPaid / r.effectiveTotalBilled) * 100).toFixed(1)}% of billed`
                  : "No draws invoiced"}
              </div>
            </div>
            <div className="px-5 py-4">
              <div className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold mb-1">
                Accounts Receivable
              </div>
              <div
                className={`text-lg font-bold ${r.accountsReceivable > 0 ? "text-orange-300" : "text-gray-400"}`}
              >
                {fmt(r.accountsReceivable)}
              </div>
              <div className="text-[10px] text-gray-400">
                Billed but not yet paid
              </div>
            </div>
            <div className="px-5 py-4">
              <div className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold mb-1">
                WIP Completion
              </div>
              <div className="text-lg font-bold text-orange-400">
                {r.wipCompletionPct.toFixed(1)}%
              </div>
              <div className="text-[10px] text-gray-400">
                Cost incurred / budget
              </div>
            </div>
          </div>

          {Math.abs(r.billingGapPct) > 2 && (
            <div
              className={`mx-5 my-3 px-4 py-3 rounded-lg flex items-start gap-3 border ${
                r.billingGapPct > 0
                  ? "bg-red-900/20 border-red-700/40"
                  : "bg-yellow-900/20 border-yellow-700/40"
              }`}
            >
              <div
                className={`mt-0.5 text-sm flex-shrink-0 ${r.billingGapPct > 0 ? "text-red-400" : "text-yellow-400"}`}
              >
                ⚠
              </div>
              <div>
                <div
                  className={`text-xs font-semibold ${r.billingGapPct > 0 ? "text-red-400" : "text-yellow-400"}`}
                >
                  {r.billingGapPct > 0
                    ? `Overbilled — ${Math.abs(r.billingGapPct).toFixed(1)}% ahead of completion`
                    : `Underbilled — ${Math.abs(r.billingGapPct).toFixed(1)}% behind completion`}
                </div>
                <div className="text-[11px] text-gray-400 mt-0.5">
                  {r.billingGapPct > 0
                    ? `Billed ${r.billingPct.toFixed(1)}% of contract but WIP progress is ${r.wipCompletionPct.toFixed(1)}%. Billing ahead of completed work by ~${fmt(Math.abs(r.billingGapAmt))}.`
                    : `Billed ${r.billingPct.toFixed(1)}% of contract but WIP progress is ${r.wipCompletionPct.toFixed(1)}%. About ${fmt(Math.abs(r.billingGapAmt))} of completed work has not been invoiced yet.`}
                </div>
              </div>
            </div>
          )}

          <div className="px-5 py-4">
            <div className="flex items-center justify-between text-[11px] text-gray-400 mb-3">
              <span>Billing vs completion comparison</span>
              <span>Contract: {fmt(r.currentContractValue)}</span>
            </div>
            <div className="space-y-2.5">
              <div>
                <div className="flex justify-between text-[10px] text-gray-400 mb-1">
                  <span>Billed to date</span>
                  <span className="text-green-400 font-medium">
                    {fmt(r.effectiveTotalBilled)} · {r.billingPct.toFixed(1)}%
                  </span>
                </div>
                <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-green-500 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(100, r.billingPct)}%` }}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-[10px] text-gray-400 mb-1">
                  <span>WIP completion</span>
                  <span className="text-orange-400 font-medium">
                    {fmt(r.totalCharged)} · {r.wipCompletionPct.toFixed(1)}%
                  </span>
                </div>
                <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-orange-400 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(100, r.wipCompletionPct)}%` }}
                  />
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
              <div className="flex items-center gap-5">
                <span className="flex items-center gap-1.5 text-[11px] text-gray-400">
                  <span className="w-2.5 h-2.5 rounded-sm bg-green-500 inline-block" />
                  Billed to date
                </span>
                <span className="flex items-center gap-1.5 text-[11px] text-gray-400">
                  <span className="w-2.5 h-2.5 rounded-sm bg-orange-400 inline-block" />
                  WIP completion
                </span>
              </div>
              <span
                className={`text-[10px] font-semibold px-2.5 py-1 rounded-full border ${
                  Math.abs(r.billingGapPct) <= 2
                    ? "bg-green-900/30 text-green-400 border-green-700/50"
                    : r.billingGapPct > 0
                      ? "bg-red-900/30 text-red-400 border-red-700/50"
                      : "bg-yellow-900/30 text-yellow-400 border-yellow-700/50"
                }`}
              >
                {Math.abs(r.billingGapPct) <= 2
                  ? "Billing on track"
                  : r.billingGapPct > 0
                    ? `Overbilled ${r.billingGapPct.toFixed(1)}%`
                    : `Underbilled ${Math.abs(r.billingGapPct).toFixed(1)}%`}
              </span>
            </div>
          </div>
        </div>

        {/* ── Tabs ── */}
        <div className="flex gap-1 bg-gray-800 p-1 rounded-lg border border-gray-700 w-fit">
          {[
            { key: "labour", label: `Labour (${labourWOs.length})` },
            { key: "materials", label: `Materials (${pos.length})` },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-5 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? "bg-orange-500 text-white shadow"
                  : "text-gray-400 hover:text-white hover:bg-gray-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Labour tab ── */}
        {activeTab === "labour" && (
          <div className="space-y-6">
            <LabourGroup
              title="Piecework"
              icon="🔧"
              prefix="pw"
              wos={pieceworkWOs}
              totals={sumGroup(pieceworkWOs)}
              openAccordions={openAccordions}
              toggleAccordion={toggleAccordion}
              token={token}
              onAgreementChanged={refresh}
            />
            <LabourGroup
              title="Hourly"
              icon="⏱"
              prefix="hr"
              wos={hourlyWOs}
              totals={sumGroup(hourlyWOs)}
              openAccordions={openAccordions}
              toggleAccordion={toggleAccordion}
              token={token}
              onAgreementChanged={refresh}
            />
            <LabourGroup
              title="Subcontractor"
              icon="🏗"
              prefix="sc"
              wos={subcontractorWOs}
              totals={sumGroup(subcontractorWOs)}
              openAccordions={openAccordions}
              toggleAccordion={toggleAccordion}
              token={token}
              onAgreementChanged={refresh}
            />
            <LabourGroup
              title="Other"
              icon="📋"
              prefix="ot"
              wos={otherWOs}
              totals={sumGroup(otherWOs)}
              openAccordions={openAccordions}
              toggleAccordion={toggleAccordion}
              token={token}
              onAgreementChanged={refresh}
            />

            {labourWOs.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                No work orders found for this project.
              </div>
            )}
          </div>
        )}

        {/* ── Materials tab ── */}
        {activeTab === "materials" && (
          <div>
            <SectionHeader
              title="Purchase Orders"
              icon="📦"
              count={pos.length}
              budget={matTotals.budget}
              charged={matTotals.ordered}
              pending={matTotals.pending}
              remaining={matTotals.remaining}
            />
            {r.totalSurcharges !== 0 && (
              <p className="text-[11px] text-gray-400 mb-3">
                Material order surcharges:{" "}
                <span className="text-amber-300 font-medium">
                  {fmtExact(r.totalSurchargesDelivered)}
                </span>{" "}
                on delivered orders (in Delivered),{" "}
                <span className="text-amber-300 font-medium">
                  {fmtExact(r.totalSurchargesPending)}
                </span>{" "}
                on undelivered orders (in Pending). Open a PO to see them.
              </p>
            )}
            <div className="space-y-2">
              {pos.map((po) => {
                const key = `po-${po._id}`;
                return (
                  <AccordionCard
                    key={key}
                    isOpen={!!openAccordions[key]}
                    onToggle={() => toggleAccordion(key)}
                    header={<POAccordionHeader po={po} />}
                  >
                    {openAccordions[key] && (
                      <MaterialDrillDown poId={po._id} token={token} po={po} />
                    )}
                  </AccordionCard>
                );
              })}
              {pos.length === 0 && (
                <div className="text-center py-12 text-gray-400">
                  No purchase orders found for this project.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
